/* A viewpoint's loaded graph turned into plotted points, for every chart over the XYZ contract.
 *
 * Shared rather than written per view, because the four charts differ in almost none of it: they
 * read the same fields off the same components through the same reader, and exclude the same
 * components for the same reasons. What they do not share is what counts as too little to draw -
 * a surface needs three distinct positions to triangulate, a scatter needs one point - and whether
 * the height field is read at all. Those are the two parameters. */

import type { ArdoqId, APIFieldType } from '@ardoq/api-types';
import { APIFieldType as FieldType } from '@ardoq/api-types';
import type { FormattingAndLabelRules } from '@ardoq/data-model';
import type { FilterColorData } from '@ardoq/scope-data';
import type { PlottedContext } from './plottedContext';
import type { PermissionContext } from '@ardoq/access-control';
import type { I18nLocale } from '@ardoq/i18n';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import {
  resolveXyzFields,
  toXyzDataPoints,
  toXyzValues,
  type RequiredDimension,
  type XyzDataPoint,
  type XyzFieldSelection,
} from '@ardoq/xyz-charts';
import { getFieldsForViewpoint } from 'streams/fields/getFieldsForViewpoint';
import { toFieldOptions } from 'tabview/chartShared/fieldLabel';
import { createScopeDataReader } from './readers';
import { toComponentLabels } from 'tabview/chartShared/componentLabels';
import type { ChartEmptyReason, NumericField } from 'tabview/chartShared/types';

export type XyzPointsArgs = {
  context: PlottedContext;
  loadedGraph: LoadedGraphWithViewpointMode;
  permissionContext: PermissionContext;
  i18nLocale: I18nLocale;
  formattingAndLabelRules: FormattingAndLabelRules;
  /**
   * Conditional-formatting colors for the plotted components.
   *
   * Threaded rather than read off `filterCache` here. That module is a mutable singleton warmed per
   * view, so reading it inside the builder hides the dependency from this contract and skips the
   * warming step entirely - the cache answers with whatever the previously rendered view left in
   * it, which on a fresh load is nothing and on a switch is another traversal's components.
   */
  filterColorData: FilterColorData;
  fieldSelection: XyzFieldSelection;
  /**
   * How many distinct (x, y) positions the chart needs before it can draw anything.
   *
   * Three for the gridded charts, which triangulate and need an area to interpolate over; one for
   * the point charts, which plot what they are given. This is the difference that makes score
   * fields a dead end for a surface and perfectly fine for a scatter.
   */
  minimumDistinctPositions: number;
};

export type XyzPoints = {
  points: XyzDataPoint[];
  /** Components left out for having no value on a required field, by field. */
  excludedByDimension: Record<RequiredDimension, number>;
  excludedCount: number;
  /** Every numeric field on the plotted component types, for the field pickers. */
  numericFields: NumericField[];
  emptyReason: ChartEmptyReason;
};

/** A fresh zeroed tally per call, so no two results can share one object to mutate. */
export const noExclusions = (): Record<RequiredDimension, number> => ({
  x: 0,
  y: 0,
  z: 0,
});

const NUMERIC_FIELD_TYPES: APIFieldType[] = [FieldType.NUMBER];

/**
 * Distinct plotted positions, which is what decides whether a gridded chart is possible at all.
 *
 * Counted here rather than inferred from an empty grid so the empty state can distinguish "no
 * components" from "every component sits on the same two positions" - the second is common on
 * score fields and the fix for it is different.
 */
const countDistinctPositions = (points: XyzDataPoint[]): number =>
  new Set(points.map(({ x, y }) => `${x}|${y}`)).size;

export const buildXyzPoints = ({
  context,
  loadedGraph,
  permissionContext,
  i18nLocale,
  formattingAndLabelRules,
  filterColorData,
  fieldSelection,
  minimumDistinctPositions,
}: XyzPointsArgs): XyzPoints => {
  const enhancedScopeData = loadedGraph.scopeData;
  const componentIds = loadedGraph.scopeComponentIds;

  const relevantComponentTypeIds = new Set<ArdoqId>(
    componentIds
      .map(
        componentId =>
          enhancedScopeData.componentsById[componentId]?.typeId ?? null
      )
      .filter((typeId): typeId is string => typeId !== null)
  );

  const numericFields = toFieldOptions(
    getFieldsForViewpoint({
      scopeData: enhancedScopeData,
      permissionContext,
      workspaceIds: context.workspaceIds,
      types: NUMERIC_FIELD_TYPES,
      relevantComponentTypeIds,
    })
  );

  const nothing = (emptyReason: ChartEmptyReason): XyzPoints => ({
    points: [],
    excludedByDimension: noExclusions(),
    excludedCount: 0,
    numericFields,
    emptyReason,
  });

  if (componentIds.length === 0) return nothing('no components');
  if (numericFields.length === 0) return nothing('no numeric fields');

  /* Scoped, as `resolveXyzFields` requires: these settings persist per user across viewpoints, so a
     field picked over Applications survives into a viewpoint over Servers - where the pickers read
     as empty while every component is silently excluded for having no value. */
  const fields = resolveXyzFields(
    fieldSelection,
    new Set(numericFields.map(({ name }) => name))
  );
  if (!fields) return nothing('no fields selected');

  const labels = toComponentLabels({
    enhancedScopeData,
    componentIds,
    formattingAndLabelRules,
    i18nLocale,
  });

  const values = toXyzValues({
    reader: createScopeDataReader({
      enhancedScopeData,
      filterColorData,
      labels,
    }),
    componentIds,
    fields,
    highlightedComponentId: context.componentId || null,
  });

  const { points, excludedByDimension, excludedCount } = toXyzDataPoints({
    values,
  });

  /* Reported with the points still attached, so the empty state can say how many components were
     found as well as why they cannot be drawn - "180 components, all on 4 positions" is a
     different problem from "no components", and the remedy is different too. */
  if (countDistinctPositions(points) < minimumDistinctPositions) {
    return {
      points,
      excludedByDimension,
      excludedCount,
      numericFields,
      emptyReason: 'too few positions',
    };
  }

  return {
    points,
    excludedByDimension,
    excludedCount,
    numericFields,
    emptyReason: null,
  };
};
